import { useEffect, useRef, useState, type ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { ArrowUpRight, Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ZmileLockup } from "@/components/ZmileMark"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { MarketingFooter } from "@/types/marketing"

type NavigationItem = {
  href: string
  label: string
}

type LanguageSwitch = {
  accessible_label: string
  href: string
  label: string
  locale: "hr" | "en"
}

type MarketingLayoutProps = {
  children: ReactNode
  currentPageKey: string
  footer: MarketingFooter
  homeHref: string
  languageSwitch: LanguageSwitch
  locale: "hr" | "en"
  navigation: NavigationItem[]
  consultationHref: string
}

export function MarketingLayout({
  children,
  currentPageKey,
  footer,
  homeHref,
  languageSwitch,
  locale,
  navigation,
  consultationHref,
}: MarketingLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const consultationLabel =
    locale === "hr"
      ? "Zatražite besplatne 30-minutne konzultacije"
      : "Request a free 30-minute consultation"
  const menuLabel = locale === "hr" ? "Otvori navigaciju" : "Open navigation"
  const pagePaths: Record<string, string> =
    locale === "hr"
      ? { work: "/radovi", services: "/usluge", about: "/o-nama" }
      : { work: "/en/work", services: "/en/services", about: "/en/about" }
  const currentPageHref = pagePaths[currentPageKey]
  const trackLanguageChange = () =>
    captureAnalyticsEvent("language_changed", {
      locale: languageSwitch.locale,
      page_id: currentPageKey,
    })

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  useEffect(() => {
    const root = document.documentElement
    const restoreDarkTheme = root.classList.contains("dark")
    root.classList.remove("dark")

    return () => {
      if (restoreDarkTheme) root.classList.add("dark")
    }
  }, [])

  useEffect(() => {
    if (!menuOpen) return

    const closeMenu = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return

      setMenuOpen(false)
      menuButtonRef.current?.focus()
    }
    document.addEventListener("keydown", closeMenu)
    return () => document.removeEventListener("keydown", closeMenu)
  }, [menuOpen])

  return (
    <div className="marketing-site">
      <a className="marketing-skip-link" href="#main-content">
        {locale === "hr"
          ? "Preskoči na glavni sadržaj"
          : "Skip to main content"}
      </a>
      <header className="marketing-header">
        <Link className="marketing-brand" href={homeHref} aria-label="Zmile">
          <ZmileLockup
            accent="#1f8a56"
            gap={10}
            lineColor="var(--color-ink-display)"
            size={36}
          />
        </Link>

        <nav
          className="marketing-desktop-nav"
          aria-label={
            locale === "hr" ? "Glavna navigacija" : "Primary navigation"
          }
        >
          {navigation.map((item) => (
            <Link
              className="marketing-nav-link type-marketing-nav"
              href={item.href}
              key={item.href}
              aria-current={item.href === currentPageHref ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="marketing-header-actions">
          <Link
            className="marketing-language-link type-marketing-action"
            href={languageSwitch.href}
            aria-label={languageSwitch.accessible_label}
            hrefLang={languageSwitch.locale}
            onClick={trackLanguageChange}
          >
            {languageSwitch.label}
          </Link>
          {currentPageKey === "contact" ? (
            <span
              className="btn btn-marketing btn-marketing-small marketing-current-cta"
              aria-current="page"
            >
              <span className="marketing-cta-long">{consultationLabel}</span>
              <span className="marketing-cta-short">
                {locale === "hr" ? "30-min konzultacije" : "30-minute call"}
              </span>
              <ArrowUpRight aria-hidden="true" />
            </span>
          ) : (
            <Button asChild variant="marketing" size="marketingSm">
              <Link
                href={consultationHref}
                aria-label={consultationLabel}
                data-analytics-id="header-consultation"
              >
                <span className="marketing-cta-long">{consultationLabel}</span>
                <span className="marketing-cta-short">
                  {locale === "hr" ? "30-min konzultacije" : "30-minute call"}
                </span>
                <ArrowUpRight aria-hidden="true" />
              </Link>
            </Button>
          )}
          <button
            ref={menuButtonRef}
            type="button"
            className="marketing-menu-button"
            aria-controls="marketing-mobile-nav"
            aria-expanded={menuOpen}
            aria-label={
              menuOpen
                ? locale === "hr"
                  ? "Zatvori navigaciju"
                  : "Close navigation"
                : menuLabel
            }
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <nav
          className="marketing-mobile-nav"
          id="marketing-mobile-nav"
          aria-label={
            locale === "hr" ? "Mobilna navigacija" : "Mobile navigation"
          }
        >
          {navigation.map((item) => (
            <Link
              href={item.href}
              key={item.href}
              aria-current={item.href === currentPageHref ? "page" : undefined}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link
            href={languageSwitch.href}
            hrefLang={languageSwitch.locale}
            onClick={() => {
              trackLanguageChange()
              setMenuOpen(false)
            }}
          >
            {languageSwitch.accessible_label}
          </Link>
          <Link
            href={footer.consultation_href}
            onClick={() => setMenuOpen(false)}
          >
            {footer.consultation_label}
          </Link>
        </nav>
      )}

      <main id="main-content" tabIndex={-1}>
        {children}
      </main>

      <footer className="marketing-footer">
        <div className="marketing-footer-brand">
          <strong>{footer.company_name}</strong>
          <p>
            {locale === "hr"
              ? "Web-stranice, praktični AI sustavi i poslovna automatizacija."
              : "Websites, practical AI systems, and business automation."}
          </p>
          <address className="marketing-footer-legal type-marketing-body-small">
            {footer.company_address}
            <br />
            {footer.company_registration}
          </address>
        </div>
        <nav
          className="marketing-footer-links"
          aria-label={locale === "hr" ? "Podnožje" : "Footer"}
        >
          {navigation.map((item) => (
            <Link href={item.href} key={item.href}>
              {item.label}
            </Link>
          ))}
          <Link href={footer.website_review_href}>
            {footer.website_review_label}
          </Link>
          <Link href={footer.consultation_href}>
            {footer.consultation_label}
          </Link>
          <a href={`mailto:${footer.email}`}>
            {footer.email_prompt} <span>{footer.email}</span>
          </a>
          <Link href={footer.privacy_href}>{footer.privacy_label}</Link>
        </nav>
        <p className="marketing-copyright">© {footer.company_name}</p>
      </footer>
    </div>
  )
}
